import { fetcher, formatLongDate, sanitizeFilename } from '@commenda/utils';
import { useState } from 'react';

// Mirrors src/commenda-os/business-entity/reports/reports.types.ts.
// 'organization-chart' / 'tax-chart' are FE-only labels used for filename
// and modal routing — on the wire they always travel as composite sections
// with their captured PNG in the body. The BE's REPORT_KINDS doesn't
// include them.
export type ReportKind =
  | 'entity'
  | 'business-info'
  | 'ownership'
  | 'key-persons'
  | 'organization-chart'
  | 'tax-chart';

// Composite sections. Equivalent to ReportKind minus the composite root —
// nesting 'entity' inside itself is undefined.
export type EntitySection = Exclude<ReportKind, 'entity'>;

// Display titles drive the saved-file name on disk. Keep in sync with the
// BE's REPORT_TITLES map so what the BE writes in Content-Disposition and
// what the browser names the download match.
const REPORT_TITLES: Record<ReportKind, string> = {
  entity: 'Entity Overview Report',
  'business-info': 'Business Information Report',
  ownership: 'Ownership and Control Report',
  'key-persons': 'Key Persons & Roles Report',
  'organization-chart': 'Organization Chart Report',
  'tax-chart': 'Tax Chart Report',
};

// Base64 PNG data URIs, keyed to match the EntitySection ids that
// consume them on the server.
export interface EntityReportAttachments {
  organizationChartPng?: string;
  taxChartPng?: string;
}

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameFor = (kind: ReportKind, businessEntityName?: string | null): string => {
  const name = sanitizeFilename(businessEntityName, 'Entity');
  return `${name} - ${REPORT_TITLES[kind]} - ${formatLongDate()}.pdf`;
};

const readError = async (response: Response, kind: ReportKind): Promise<string> => {
  let message = `Failed to export ${REPORT_TITLES[kind]}`;
  try {
    message = (await response.json())?.message ?? message;
  } catch {
    /* non-JSON body */
  }
  return message;
};

// GET path: single-section exports (business-info / ownership / key-persons)
// and the composite without any chart attachments. Composite-only sections
// (organization-chart, tax-chart) require the POST path.
const exportViaGet = async (
  businessEntityId: number,
  kind: ReportKind,
  sections?: EntitySection[],
): Promise<Blob> => {
  const params = new URLSearchParams({ format: 'pdf' });
  if (kind === 'entity' && sections && sections.length > 0) {
    params.set('sections', sections.join(','));
  }
  const response = await fetcher(
    `business-entities/${businessEntityId}/reports/${kind}?${params.toString()}`,
    { method: 'GET' },
  );
  if (!response.ok) throw new Error(await readError(response, kind));
  return response.blob();
};

// POST path: composite exports that include captured chart attachments.
// PNGs ride along as base64 data URIs; the BE caps payload size and
// validates magic bytes before embedding.
const exportComposite = async (
  businessEntityId: number,
  sections: EntitySection[],
  attachments?: EntityReportAttachments,
): Promise<Blob> => {
  // fetcher() JSON-stringifies `body` internally and sets the JSON content
  // type by default — pass the object as-is.
  const response = await fetcher(
    `business-entities/${businessEntityId}/reports/entity`,
    {
      method: 'POST',
      body: { format: 'pdf', sections, attachments },
    },
  );
  if (!response.ok) throw new Error(await readError(response, 'entity'));
  return response.blob();
};

// Composite is required when any chart section is selected (charts are not
// standalone), and when the user picked more than one section in the modal.
const CHART_SECTIONS: ReadonlySet<EntitySection> = new Set<EntitySection>([
  'organization-chart',
  'tax-chart',
]);

const requiresCompositePost = (
  sections: EntitySection[] | undefined,
  attachments: EntityReportAttachments | undefined,
): boolean => {
  if (attachments && (attachments.organizationChartPng || attachments.taxChartPng)) return true;
  return Boolean(sections?.some((s) => CHART_SECTIONS.has(s)));
};

// `kind` drives the saved filename. The wire-format routing is:
//   - 'organization-chart' / 'tax-chart' → composite POST with a single
//     section (the BE short-circuits to a standalone portrait layout for
//     single-chart selections). No dedicated chart endpoint exists.
//   - 'entity' with chart attachments → composite POST.
//   - Everything else → existing GET path (unchanged).
// Browsers ignore Content-Disposition on fetch+Blob downloads, so the
// filename is recomputed here to match what the BE writes.
export const exportBusinessEntityReport = async (
  businessEntityId: number,
  kind: ReportKind,
  businessEntityName?: string | null,
  sections?: EntitySection[],
  attachments?: EntityReportAttachments,
): Promise<void> => {
  let blob: Blob;
  if (kind === 'organization-chart' || kind === 'tax-chart') {
    blob = await exportComposite(businessEntityId, [kind], attachments);
  } else if (kind === 'entity' && requiresCompositePost(sections, attachments)) {
    blob = await exportComposite(businessEntityId, sections ?? [], attachments);
  } else {
    blob = await exportViaGet(businessEntityId, kind, sections);
  }
  triggerBlobDownload(blob, filenameFor(kind, businessEntityName));
};

export function useExportBusinessEntityReport({
  businessEntityId,
  businessEntityName,
  onError,
}: {
  businessEntityId: number;
  businessEntityName?: string | null;
  onError?: (message: string) => void;
}) {
  // Per-kind exporting state so multiple menu items can be clicked in quick
  // succession without one disabling the others.
  const [exportingKind, setExportingKind] = useState<ReportKind | null>(null);
  const exportReport = async (
    kind: ReportKind,
    sections?: EntitySection[],
    attachments?: EntityReportAttachments,
  ) => {
    if (exportingKind !== null) return;
    setExportingKind(kind);
    try {
      await exportBusinessEntityReport(businessEntityId, kind, businessEntityName, sections, attachments);
    } catch (err) {
      onError?.((err as Error).message);
    } finally {
      setExportingKind(null);
    }
  };
  return { exportingKind, exportReport };
}
